import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { paymentsI18n } from './i18n';

const normalizeStatus = (status) => {
  const normalized = String(status || '')
    .trim()
    .toLowerCase();

  if (normalized === 'inactive' || normalized === 'inactiva') {
    return 'inactive';
  }

  return 'active';
};

export default function CreatePaymentMethodModal({
  Modal,
  Input,
  Dropdown,
  show,
  onClose,
  mode = 'auto',
  initialData = null,
  onSave,
  onRequestDelete,
  width = '720px',
}) {
  const { language } = useLanguage();
  const paymentsLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: paymentsI18n });

  const isEdit = mode === 'edit' || (mode === 'auto' && !!initialData);

  useEffect(() => {
    paymentsI18n.changeLanguage(paymentsLanguage);
  }, [paymentsLanguage]);

  const typeOptions = [
    { label: t('payments_modal_type_credit'), value: 'credit' },
    { label: t('payments_modal_type_debit'), value: 'debit' },
  ];

  const brandOptions = [
    { label: 'Visa', value: 'Visa' },
    { label: 'Mastercard', value: 'Mastercard' },
    { label: 'American Express', value: 'Amex' },
  ];

  const networkOptions = [
    { label: 'Kushki', value: 'Kushki' },
    { label: 'RedCompra', value: 'RedCompra' },
    { label: t('payments_modal_network_other'), value: 'Otro' },
  ];

  const statusOptions = [
    { label: t('payments_modal_state_active'), value: 'active' },
    { label: t('payments_modal_state_inactive'), value: 'inactive' },
  ];

  const optionFrom = (options) => (value) =>
    options.find((option) => option.value === value) ?? null;

  const [form, setForm] = useState({
    type: 'credit',
    brand: '',
    holderName: '',
    last4: '',
    expiryMonth: '',
    expiryYear: '',
    network: '',
    status: 'active',
  });

  useEffect(() => {
    if (!show) return;

    if (isEdit && initialData) {
      let expiryMonth = initialData.expiryMonth || '';
      let expiryYear = initialData.expiryYear || '';

      if (!expiryMonth && initialData.expires) {
        const [month, year] = String(initialData.expires).split('/');
        if (month) expiryMonth = month.trim();
        if (year) {
          const trimmedYear = year.trim();
          expiryYear = trimmedYear.length === 2 ? `20${trimmedYear}` : trimmedYear;
        }
      }

      setForm({
        type: initialData.type || 'credit',
        brand: initialData.brand || '',
        holderName: initialData.holderName || initialData.holder || '',
        last4:
          initialData.last4 ||
          initialData.lastFour ||
          initialData.cardLast4 ||
          '',
        expiryMonth: expiryMonth || '',
        expiryYear: expiryYear || '',
        network: initialData.network || '',
        status: normalizeStatus(initialData.status),
      });
    } else {
      setForm({
        type: 'credit',
        brand: '',
        holderName: '',
        last4: '',
        expiryMonth: '',
        expiryYear: '',
        network: '',
        status: 'active',
      });
    }
  }, [show, isEdit, initialData]);

  const onChange = (key) => (event) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const last4Valid = useMemo(() => {
    if (!form.last4) return false;
    const digits = form.last4.replace(/\D/g, '');
    return digits.length === 4;
  }, [form.last4]);

  const monthValid = useMemo(() => {
    if (!form.expiryMonth) return false;
    const digits = form.expiryMonth.replace(/\D/g, '');
    const numeric = Number(digits);
    return digits.length === 2 && numeric >= 1 && numeric <= 12;
  }, [form.expiryMonth]);

  const yearValid = useMemo(() => {
    if (!form.expiryYear) return false;
    const digits = form.expiryYear.replace(/\D/g, '');
    return digits.length === 4;
  }, [form.expiryYear]);

  const isValid = useMemo(() => {
    return (
      (form.type === 'credit' || form.type === 'debit') &&
      form.brand.trim() &&
      form.holderName.trim() &&
      last4Valid &&
      monthValid &&
      yearValid &&
      form.network.trim() &&
      (form.status === 'active' || form.status === 'inactive')
    );
  }, [form, last4Valid, monthValid, yearValid]);

  const handleSave = () => {
    if (!isValid) return;

    const payload = {
      ...(isEdit && initialData?.id ? { id: initialData.id } : {}),
      type: form.type,
      brand: form.brand.trim(),
      holderName: form.holderName.trim(),
      last4: form.last4.replace(/\D/g, '').slice(-4),
      expiryMonth: form.expiryMonth.replace(/\D/g, ''),
      expiryYear: form.expiryYear.replace(/\D/g, ''),
      network: form.network.trim(),
      status: form.status,
    };

    onSave?.(payload);
  };

  const typeOptionFrom = optionFrom(typeOptions);
  const brandOptionFrom = optionFrom(brandOptions);
  const networkOptionFrom = optionFrom(networkOptions);
  const statusOptionFrom = optionFrom(statusOptions);

  return (
    <Modal
      title={
        isEdit ? t('payments_modal_title_edit') : t('payments_modal_title_create')
      }
      show={show}
      onClose={onClose}
      primaryBtnTxt={t('payments_modal_save')}
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid}
      secondaryBtnTxt={t('payments_modal_cancel')}
      secondaryBtnAction={onClose}
      tertiaryBtnTxt={
        isEdit && onRequestDelete ? t('payments_modal_delete') : undefined
      }
      tertiaryBtnAction={
        isEdit && onRequestDelete ? onRequestDelete : undefined
      }
      tertiaryBtnVariant={isEdit && onRequestDelete ? 'danger' : undefined}
      ctaAligment='right'
      width={width}
    >
      <div className='row g-3'>
        <div className='col-12 col-md-6'>
          <div className='d-flex'>
            <label className='form-label'>{t('payments_modal_card_type')}</label>
          </div>
          <Dropdown
            options={typeOptions}
            value={typeOptionFrom(form.type)}
            onChange={(option) =>
              setForm((prev) => ({ ...prev, type: option?.value || 'credit' }))
            }
            placeholder={t('payments_modal_card_type_placeholder')}
            width='100%'
          />
        </div>

        <div className='col-12 col-md-6'>
          <div className='d-flex'>
            <label className='form-label'>{t('payments_modal_brand')}</label>
          </div>
          <Dropdown
            options={brandOptions}
            value={brandOptionFrom(form.brand)}
            onChange={(option) =>
              setForm((prev) => ({ ...prev, brand: option?.value || '' }))
            }
            placeholder={t('payments_modal_brand_placeholder')}
            width='100%'
          />
        </div>

        <div className='col-12'>
          <Input
            label={t('payments_modal_holder_name')}
            required
            placeholder={t('payments_modal_holder_name_placeholder')}
            value={form.holderName}
            onChange={onChange('holderName')}
          />
        </div>

        <div className='col-12 col-md-6'>
          <Input
            label={t('payments_modal_last4')}
            required
            placeholder={t('payments_modal_last4_placeholder')}
            value={form.last4}
            onChange={onChange('last4')}
            error={
              form.last4 && !last4Valid ? t('payments_modal_last4_error') : ''
            }
            inputMode='numeric'
            maxLength={4}
          />
        </div>

        <div className='col-12 col-md-6'>
          <div className='row g-2'>
            <div className='col-6'>
              <Input
                label={t('payments_modal_expiry_month')}
                required
                placeholder={t('payments_modal_expiry_month_placeholder')}
                value={form.expiryMonth}
                onChange={onChange('expiryMonth')}
                error={
                  form.expiryMonth && !monthValid
                    ? t('payments_modal_expiry_month_error')
                    : ''
                }
                inputMode='numeric'
                maxLength={2}
              />
            </div>
            <div className='col-6'>
              <Input
                label={t('payments_modal_expiry_year')}
                required
                placeholder={t('payments_modal_expiry_year_placeholder')}
                value={form.expiryYear}
                onChange={onChange('expiryYear')}
                error={
                  form.expiryYear && !yearValid
                    ? t('payments_modal_expiry_year_error')
                    : ''
                }
                inputMode='numeric'
                maxLength={4}
              />
            </div>
          </div>
        </div>

        <div className='col-12 col-md-6'>
          <div className='d-flex'>
            <label className='form-label'>{t('payments_modal_processor')}</label>
          </div>
          <Dropdown
            options={networkOptions}
            value={networkOptionFrom(form.network)}
            onChange={(option) =>
              setForm((prev) => ({ ...prev, network: option?.value || '' }))
            }
            placeholder={t('payments_modal_processor_placeholder')}
            width='100%'
          />
        </div>

        <div className='col-12 col-md-6'>
          <div className='d-flex'>
            <label className='form-label'>{t('payments_modal_state')}</label>
          </div>
          <Dropdown
            options={statusOptions}
            value={statusOptionFrom(form.status)}
            onChange={(option) =>
              setForm((prev) => ({ ...prev, status: option?.value || 'active' }))
            }
            placeholder={t('payments_modal_state_placeholder')}
            width='100%'
          />
        </div>
      </div>
    </Modal>
  );
}
